<template>
  <v-card
    class="rounded-xl"
    color="gray7"
    :height="height"
    :min-height="height"
    :max-height="height"
  >
    <div
      class="d-flex flex-column fill-height"
      @keypress.prevent.enter="submit()"
    >
      <v-app-bar
        class="pa-0 rounded-t-xl rounded-br-xl elevation-2"
        color="gray7"
        style="position: sticky; top: 0px; z-index: 9"
        top
      >
        <img src="@/assets/mini_logo.png" alt="logo" class="imgLogo" />

        <div v-if="title" class="text-body-1 font-weight-bold">
          <span class="title-height" v-html="title" />
        </div>

        <div v-else-if="attendant && attendant.name" class="text-h6">
          {{ handleName(attendant.name) }}
        </div>

        <v-spacer />

        <slot name="menu">
          <!-- O menu tinha uma opção só: escondia a única ação do cabeçalho
          atrás de dois cliques. Agora o botão é direto. Em tela estreita fica
          só o ícone, com o rótulo no title, para não competir com o nome do
          atendente. -->
          <v-btn
            v-if="!readMode"
            class="custom-btn text-none"
            :title="finalizeLabel"
            :aria-label="finalizeLabel"
            :icon="$vuetify.breakpoint.xsOnly"
            :text="!$vuetify.breakpoint.xsOnly"
            small
            @click="handleFinalize()"
          >
            <v-icon :left="!$vuetify.breakpoint.xsOnly" small>mdi-close</v-icon>

            <span v-if="!$vuetify.breakpoint.xsOnly">{{ finalizeLabel }}</span>
          </v-btn>
        </slot>
      </v-app-bar>

      <!-- chat -->
      <div class="d-flex flex-column scroll-area fill-height my-2">
        <!-- messages -->
        <v-list class="pa-0 transparent" height="100%">
          <template v-for="(item, i) in chat.messages">
            <div
              v-show="item.message !== 'Sim, podemos seguir'"
              :key="i"
              class="my-6"
            >
              <!-- message -->
              <div
                class="d-flex align-end px-4"
                :class="{ 'flex-row-reverse text-right': item.me }"
              >
                <v-avatar
                  v-if="!item.me"
                  class="mr-2"
                  color="gray5"
                  size="2rem"
                >
                  <v-icon v-if="attendant && attendant.icon" dark small>
                    {{ attendant.icon }}
                  </v-icon>
                </v-avatar>

                <v-card
                  class="rounded-xl px-4 pt-2 pb-1"
                  :class="item.me ? 'rounded-br-0' : 'rounded-bl-0'"
                  flat
                >
                  <div class="d-flex flex-column">
                    <div class="text-body-1" :class="{ 'text-right': item.me }">
                      <a
                        v-if="isAttach(item.message) || isMeet(item.message)"
                        :href="item.message"
                        target="_blank"
                        class="d-flex flex-grow text-decoration-none"
                      >
                        <v-card color="gray7" class="py-1 px-4 rounded-lg" flat>
                          <!-- Miniatura do anexo. O ícone fica atrás da
                          imagem/PDF: se a miniatura não carregar, ele aparece
                          sozinho e o tipo do arquivo continua legível. -->
                          <div
                            v-if="isAttach(item.message)"
                            class="d-flex align-center"
                          >
                            <div class="attach-preview mr-3">
                              <v-icon class="attach-preview__icon">
                                {{ fileIcon(item.message) }}
                              </v-icon>

                              <img
                                v-if="
                                  fileKind(item.message) === 'image' &&
                                  !previewFailed[item.message]
                                "
                                :src="item.message"
                                class="attach-preview__media"
                                alt=""
                                loading="lazy"
                                @error="handlePreviewError(item.message)"
                              />

                              <iframe
                                v-else-if="
                                  embedsInIframe(item.message) &&
                                  !previewFailed[item.message]
                                "
                                :src="previewSrc(item.message)"
                                class="attach-preview__media"
                                :class="{
                                  'attach-preview__media--text':
                                    fileKind(item.message) === 'text',
                                }"
                                loading="lazy"
                                tabindex="-1"
                                title=""
                              ></iframe>
                            </div>

                            <div>
                              <div
                                class="primary--text text-decoration-underline"
                              >
                                Visualizar anexo
                              </div>

                              <div class="text-caption gray2--text">
                                {{ fileLabel(item.message) }}
                              </div>
                            </div>
                          </div>

                          <div
                            v-if="isMeet(item.message)"
                            class="d-flex align-center"
                          >
                            <div
                              class="primary--text text-decoration-underline"
                            >
                              Clique aqui para iniciar o atendimento por vídeo
                            </div>

                            <v-icon right small>mdi-video-box</v-icon>
                          </div>
                        </v-card>
                      </a>

                      <span v-else v-html="formattedMessage(item.message)" />
                    </div>

                    <!-- response options -->
                    <v-item-group
                      v-if="item.options && item.options.length"
                      class="my-2"
                    >
                      <v-item
                        v-for="(option, n) in item.options"
                        v-slot="{ active, toggle }"
                        :key="n"
                        @change="handleOption(option)"
                      >
                        <v-chip
                          class="ma-1 px-5"
                          active-class="orange white--text"
                          :input-value="active"
                          :disabled="i < chat.messages.length - 1 || readMode"
                          :outlined="!active"
                          @click="toggle"
                        >
                          <span class="text-body-1">{{
                            option ? option.label : ""
                          }}</span>
                        </v-chip>
                      </v-item>
                    </v-item-group>

                    <div v-if="item.options?.length === 0">
                      <div class="my-4">
                        <span class="text-body-2">
                          Não temos opções disponíveis no momento, tente
                          novamente mais tarde
                        </span>
                      </div>
                    </div>

                    <div class="d-flex text-body-2">
                      <v-spacer v-if="item.me" />

                      <div class="grey--text">
                        <span v-if="showAuthor && item.author">
                          {{ item.author.name }} -
                        </span>

                        {{ formatDate(item.createdAt, "HH:mm") }}
                      </div>

                      <v-spacer v-if="!item.me" />
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </template>
        </v-list>
      </div>

      <section v-if="!readMode">
        <v-divider class="white" />

        <v-app-bar class="pa-0" color="transparent" height="auto" bottom flat>
          <v-form
            v-model="valid"
            ref="form"
            class="fill-width"
            autocomplete="off"
          >
            <div class="d-flex align-center">
              <!-- masked input -->
              <v-text-field
                v-if="!!_mask"
                v-model="message"
                v-mask="_mask"
                id="maskedInput"
                :placeholder="placeholder"
                :readonly="_hasOptions || disabled"
                :disabled="(_hasOptions && !optionValue) || disabled"
                :rules="_rules"
                background-color="transparent"
                hide-details="auto"
                dense
                solo
                flat
              />

              <!-- naked input -->
              <v-text-field
                v-show="!_mask"
                v-model="message"
                id="nakedInput"
                :placeholder="placeholder"
                :readonly="_hasOptions || disabled"
                :disabled="(_hasOptions && !optionValue) || disabled"
                :rules="_rules"
                background-color="transparent"
                hide-details="auto"
                dense
                solo
                flat
                @keydown="handlePreventSpace"
              />

              <div class="ml-2">
                <v-btn
                  v-if="!hideAttach"
                  class="mr-1"
                  :disabled="disabled || !valid"
                  small
                  icon
                  @click="attachFile()"
                  color="orange"
                >
                  <v-icon>mdi-paperclip</v-icon>
                </v-btn>

                <v-btn
                  :disabled="disabled || !valid"
                  small
                  icon
                  @click="submit()"
                  color="orange"
                >
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>
            </div>
          </v-form>
        </v-app-bar>
      </section>
    </div>

    <input
      ref="uploader"
      class="d-none"
      type="file"
      :accept="acceptedFileTypes"
      :maxlength="'25000000'"
      @change="onFileChanged($event)"
    />
  </v-card>
</template>

<script>
import { validation } from "@/utils/validators";
import { validateCpf } from "@/utils";
import { Howl, Howler } from "howler";
import { mask } from "vue-the-mask";
const moment = require("moment");
moment.locale("pt-BR");

// Espelha `allowedFileTypes` da API (files.helper.ts). Sem o accept o seletor
// oferece qualquer arquivo e a recusa só chega depois do upload inteiro.
const ACCEPTED_FILE_TYPES = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".tiff",
  ".webp",
  ".svg",
  ".pdf",
  ".txt",
  ".doc",
  ".docx",
].join(",");

export default {
  data() {
    return {
      // Miniaturas que falharam ao carregar, por URL: sem isso a imagem
      // quebrada esconderia o ícone que indica o tipo do arquivo.
      previewFailed: {},
      acceptedFileTypes: ACCEPTED_FILE_TYPES,
      valid: false,
      message: "",
      optionValue: null,
      rules: {
        required: [(v) => !!v || "Campo obrigatório"],
        email: [(v) => /.+@.+..+/.test(v) || "E-mail precisa ser válido"],
        cpf: [
          (v) =>
            (v.length === 14 && validateCpf(v)) || "CPF precisa ser válido",
        ],
        phone: [(v) => v.length === 16 || "Telefone precisa ser válido"],
      },
    };
  },

  directives: {
    mask,
  },

  props: {
    chat: {
      type: Object,
      required: true,
    },

    attendant: {
      type: Object,
      required: true,
    },

    icon: {
      type: String,
      default: "",
    },

    title: {
      type: String,
      default: "",
    },

    height: {
      type: [String, Number],
      default: "100%",
    },

    finalizeLabel: {
      type: String,
      default: "Finalizar Atendimento",
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readMode: {
      type: Boolean,
      default: false,
    },

    hideAttach: {
      type: Boolean,
      default: false,
    },

    showAuthor: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      default: "Digite aqui...",
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.scrollToEnd();
      this.playNotificationSound();
    });
  },

  watch: {
    ["chat.messages"]: {
      handler(val) {
        // scroll chat to end
        this.scrollToEnd();

        // clear validation
        if (this.$refs.form) this.$refs.form.resetValidation();

        // trigger notification sound
        if (val.length && !val[val.length - 1].me) this.playNotificationSound();

        // focus in input
        setTimeout(() => {
          if (this._mask) {
            const element = document.getElementById("maskedInput");
            if (element) element.focus();
          } else {
            const element = document.getElementById("nakedInput");
            if (element) element.focus();
          }
        }, 500);
      },
      deep: true,
    },

    message(val) {
      if (this._lastMessage?.rules?.includes("email")) {
        this.message = val.toLowerCase();
      }
    },
  },

  computed: {
    _hasOptions() {
      return !!this._lastMessage?.options?.length;
    },

    _mask() {
      if (!this._lastMessage) return false;

      return this._lastMessage.mask ? this._lastMessage.mask : false;
    },

    _rules() {
      const defaultRule = [(v) => validation(v) || "Informe um valor válido"];

      if (!this._lastMessage?.rules?.length) return defaultRule;

      return [
        ...this._lastMessage.rules
          .flatMap((key) => this.rules[key] ?? [])
          .filter((rule) => rule !== undefined),
        ...defaultRule,
      ];
    },

    _lastMessage() {
      const messages = this.chat.messages;
      return messages?.length ? messages[messages.length - 1] : false;
    },
  },

  methods: {
    submit() {
      if (!this.$refs.form.validate()) return;

      const payload = {
        message: this.message,
        value: this.optionValue,
        key: this._lastMessage?.key || null,
        next: this._lastMessage?.next || null,
      };
      this.$emit("send-message", payload);

      this.message = "";
      this.optionValue = null;
    },

    attachFile() {
      window.addEventListener("focus", () => {}, { once: true });
      this.$refs.uploader.click();
    },

    onFileChanged(event) {
      const files = Array.from(event.target.files);
      this.$emit("attach-file", { file: files[0] });
    },

    handleOption(option) {
      this.message = option.label;
      this.optionValue = option.value;

      this.submit();
    },

    handlePreventSpace(event) {
      if (this._lastMessage?.rules?.includes("email") && event.keyCode === 32) {
        event.preventDefault();
      }
    },

    scrollToEnd() {
      setTimeout(() => {
        const element = document.querySelector(".scroll-area");
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      }, 100);
    },

    playNotificationSound() {
      if (this.readMode) return;
      Howler.autoUnlock = true;

      const notificationSound = new Howl({
        src: [
          "https://storage.googleapis.com/notification-song/message-pop-alert.mp3",
        ],
        volume: 1,
      });

      notificationSound.play();
    },

    formatDate(date, format = "DD/MM/YYYY HH:mm:ss") {
      return moment(date).format(format);
    },

    isAttach(message) {
      const baseURL = "https://storage.googleapis.com";
      return message.startsWith(baseURL);
    },

    // A API renomeia o arquivo no upload (`<timestamp>_<uuid>.<ext>`), então o
    // nome original se perde — mas a extensão é preservada e é o que permite
    // escolher a miniatura e o ícone.
    fileExtension(url) {
      const path = String(url).split(/[?#]/)[0];
      const name = path.substring(path.lastIndexOf("/") + 1);
      const dot = name.lastIndexOf(".");

      return dot > -1 ? name.slice(dot + 1).toLowerCase() : "";
    },

    fileKind(url) {
      const ext = this.fileExtension(url);

      if (
        ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp", "svg"].includes(
          ext
        )
      ) {
        return "image";
      }
      if (ext === "pdf") return "pdf";
      if (["doc", "docx"].includes(ext)) return "doc";
      if (ext === "txt") return "text";

      return "file";
    },

    // O navegador renderiza PDF e texto puro dentro do iframe. DOC/DOCX não
    // têm visualizador nativo e ficam só com o ícone do tipo.
    embedsInIframe(url) {
      return ["pdf", "text"].includes(this.fileKind(url));
    },

    previewSrc(url) {
      // Os parâmetros escondem a barra do leitor de PDF; num .txt são inertes.
      return this.fileKind(url) === "pdf"
        ? `${url}#toolbar=0&navpanes=0&view=FitH`
        : url;
    },

    fileIcon(url) {
      const icons = {
        image: "mdi-file-image-outline",
        pdf: "mdi-file-pdf-box",
        doc: "mdi-file-word-outline",
        text: "mdi-file-document-outline",
        file: "mdi-paperclip",
      };

      return icons[this.fileKind(url)];
    },

    fileLabel(url) {
      const ext = this.fileExtension(url);

      return ext ? ext.toUpperCase() : "Arquivo";
    },

    handlePreviewError(url) {
      this.$set(this.previewFailed, url, true);
    },

    isMeet(message) {
      const baseURL = "https://meet.google.com";
      return message.startsWith(baseURL);
    },

    formattedMessage(message) {
      const links = message.replace(
        /(?:https?|ftp):\/\/[\n\S]+/gi,
        (match) => `<a href="${match}" target="_blank">${match}</a>`
      );

      return links;
    },

    handleAutofill(value) {
      this.message = value;

      setTimeout(() => {
        this.submit();
      }, 100);
    },

    handleFinalize() {
      this.$emit("finalize");
    },

    handleName(value) {
      const names = value.trim().split(/\s+/);

      if (names.length === 1) return names[0];
      if (names.length >= 2) return `${names[0]} ${names[1]}`;
    },
  },
};
</script>

<style src="./style.scss" lang="scss" />
